import {useEffect, useMemo} from 'react';
import {useFieldArray, useForm} from "react-hook-form";
import {useHistory} from "react-router-dom";
import useFetch from "v4/hooks/useFetch";
import {generateUrl} from "v4/services/api.service";
import {PARAMS_SUBMIT} from "v4/data/apiRoutes";

/**
 * Hook générique pour l'édition d'une liste admin (QuoteState, TaskType, etc.)
 *
 * @param {Object} options
 * @param {Array}  options.rawValuesFromAdmin - Les éléments bruts venant de l'admin
 * @param {string} options.entityCode         - Le code de l'entité (ex: "QuoteState", "TaskType")
 * @param {Function} [options.mapInputs]      - Transforme un item brut → valeur initiale du formulaire
 * @param {Function} [options.mapSubmitItem]  - Transforme un item du formulaire → payload API
 * @param {Function} [options.createEmptyItem]- Crée un item vide pour l'ajout
 */
export default function useAdminListEditor({
    rawValuesFromAdmin,
    entityCode,
    mapInputs,
    mapSubmitItem,
    createEmptyItem,
}) {
    const [{isFinished, isError}, fetchSave] = useFetch();

    const customerId = useMemo(() => rawValuesFromAdmin[0]?.customerId, [rawValuesFromAdmin]);

    const defaultMapInputs = (item) => ({
        itemId: item.id,
        value: item.name,
        extra: {},
    });

    const defaultMapSubmitItem = (item, index) => ({
        id: item.itemId,
        name: item.value,
        order: index + 1,
        "@type": entityCode,
        customerId: customerId,
    });

    const defaultCreateEmptyItem = () => ({value: '', extra: {}});

    const actualMapInputs = mapInputs || defaultMapInputs;
    const actualMapSubmitItem = mapSubmitItem || defaultMapSubmitItem;
    const actualCreateEmptyItem = createEmptyItem || defaultCreateEmptyItem;

    const inputs = useMemo(() => {
        return rawValuesFromAdmin.map(actualMapInputs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawValuesFromAdmin]);

    const fieldName = entityCode.charAt(0).toLowerCase() + entityCode.slice(1);

    const methods = useForm({
        defaultValues: {
            [fieldName]: inputs,
        },
    });

    const {fields, append, update, remove} = useFieldArray({
        control: methods.control,
        name: fieldName,
    });

    const handleSubmit = (data) => {
        fetchSave({
            url: generateUrl(PARAMS_SUBMIT, {customerId}),
            config: {
                method: "PUT",
                body: JSON.stringify({
                    id: entityCode,
                    name: entityCode,
                    code: entityCode,
                    isActive: true,
                    customerId: customerId,
                    params: data[fieldName].map((item, index) => actualMapSubmitItem(item, index, customerId)),
                }),
            },
        });
    };

    const {replace} = useHistory();
    useEffect(() => {
        if (!(isFinished && !isError)) return;

        replace('/administration/gestion_listes_admin');

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFinished, isError]);

    const onDelete = (index) => () => remove(index);

    const onAdd = () => append(actualCreateEmptyItem());

    const onExtraChange = (index, name) => (value) => {
        update(index, {...fields[index], extra: {...fields[index].extra, [name]: value}});
    };

    return {
        fields,
        handleSubmit: methods.handleSubmit(handleSubmit),
        register: methods.register,
        onDelete,
        onAdd,
        onExtraChange,
        methods,
        fieldName,
        customerId,
    };
}
